import { useEffect, useState } from "react";
import { RotateCcw, Pencil, Plus, Trash2 } from "lucide-react";
import { Button, Modal, Select, Table, DatePicker } from "../../../components";
import axios from "axios";
import {
  SUBSCRIBE_URL,
  SUBSCRIPTION_PLANS_URL,
  GET_USERS_URL,
  REFUND_SUBSCRIPTION_URL,
  REFUND_MANUAL_SUBSCRIPTION_URL,
  MANUAL_ADD_SUBSCRIPTION_URL,
  UPDATE_SUBSCRIPTION_DATES_URL,
} from "../../../features/UrlHelper";
import { handleApiError, showErrorModal } from "../../../features/errorHandler";
import { isAddressComplete } from "../../../features/address";
import { formatDateForBackend } from "../../../features/dateFormaters";

const authHeaders = () => ({
  headers: {
    Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
  },
});

// Rovnaký výpočet ako cenník (CennikPage) a backend (stripeController) – cena po zľave sa zaokrúhľuje na celé eurá
const getDiscountedPrice = (price, discount) => {
  const amount = parseFloat(price);
  return discount > 0 ? Math.round(amount * (1 - discount / 100)) : amount;
};

function SubscriptionsSection() {
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);

  // Plan modals
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    id: null,
    name: "",
    price: 0,
    currency: "EUR",
    duration: "",
    description: "",
    poradie: 0,
    favorite: false,
  });
  const [deletePlanConfirm, setDeletePlanConfirm] = useState(null);

  // Subscription modals
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subForm, setSubForm] = useState({
    user_id: "",
    plan_id: "",
    valid_from: "",
    valid_to: "",
    payment_type: "",
  });
  const [editSubModalOpen, setEditSubModalOpen] = useState(false);
  const [editSubForm, setEditSubForm] = useState({
    payment_id: null,
    user_id: "",
    plan_name: "",
    valid_from: "",
    valid_to: "",
  });
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [refundSubConfirm, setRefundSubConfirm] = useState(null);
  const [refunding, setRefunding] = useState(false);

  const getSubStatus = (sub) => {
    if (sub.is_refunded) return "refunded";
    const now = new Date();
    const from = new Date(sub.valid_from);
    const to = new Date(sub.valid_to);
    if (now < from) return "pending";
    if (now > to) return "expired";
    return "active";
  };

  const statusLabel = (status) => {
    switch (status) {
      case "active":
        return "Aktívne";
      case "expired":
        return "Vypršané";
      case "pending":
        return "Čakajúce";
      case "refunded":
        return "Refundované";
      default:
        return status;
    }
  };

  const validFromToValidTo = (valid_from, duration) => {
    if (!duration) return valid_from;
    const fromDate = new Date(valid_from);
    fromDate.setMonth(fromDate.getMonth() + duration);
    return fromDate.toISOString().split("T")[0];
  };

  useEffect(() => {
    loadPlans();
    loadSubscriptions();
    loadUsers();
  }, []);

  function loadUsers() {
    axios
      .get(GET_USERS_URL, authHeaders())
      .then((response) => setUsers(response.data))
      .catch((error) => handleApiError(error, "Nepodarilo sa načítať používateľov."));
  }

  function loadSubscriptions() {
    axios
      .get(SUBSCRIBE_URL, authHeaders())
      .then((response) => setSubscriptions(response.data))
      .catch((error) => handleApiError(error, "Nepodarilo sa načítať predplatné."));
  }

  function loadPlans(user_id) {
    axios
      .get(SUBSCRIPTION_PLANS_URL, authHeaders())
      .then((response) => {
        var data = response.data.map((plan) => {
          if (typeof plan.price === "string") {
            plan.price = parseFloat(plan.price);
          }
          return plan;
        });

        setPlans(data);
      })
      .catch((error) =>
        handleApiError(error, "Nepodarilo sa načítať plány predplatného.")
      );
  }

  const getPlanName = (planIdOrName) => {
    if (!planIdOrName) return "—";
    const plan = plans.find(
      (p) => p.id === planIdOrName || p.name === planIdOrName,
    );
    return plan ? plan.name : planIdOrName;
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return userId || "—";
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return fullName ? `${fullName} (${user.email})` : user.name || user.email;
  };

  // ── Plan CRUD ──
  const openAddPlan = () => {
    setEditingPlan(null);
    setPlanForm({
      id: null,
      name: "",
      price: 0,
      currency: "EUR",
      duration: "",
      description: "",
      poradie: 0,
      favorite: false,
      test_period: false,
    });
    setPlanModalOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      id: plan.id,
      name: plan.name,
      price: parseFloat(plan.price),
      currency: plan.currency || "EUR",
      duration: String(plan.duration),
      description: plan.description || "",
      poradie: plan.poradie ?? 0,
      favorite: plan.favorite ?? false,
      test_period: plan.test_period ?? false,
    });
    setPlanModalOpen(true);
  };

  const handlePlanSubmit = (e) => {
    e.preventDefault();
    const data = {
      id: planForm.id,
      name: planForm.name,
      price: parseFloat(planForm.price),
      currency: planForm.currency,
      duration: parseInt(planForm.duration, 10),
      description: planForm.description,
      poradie: parseInt(planForm.poradie, 10) || 0,
      favorite: planForm.favorite,
      test_period: planForm.test_period,
    };

    if (editingPlan) {
      axios
        .post(SUBSCRIPTION_PLANS_URL, data, authHeaders())
        .then(() => loadPlans())
        .catch((error) => handleApiError(error, { showAlert: true, fallbackMessage: "Chyba pri zmene plánu." }));
    } else {
      axios
        .put(SUBSCRIPTION_PLANS_URL, data, authHeaders())
        .then(() => loadPlans())
        .catch((error) => handleApiError(error, { showAlert: true, fallbackMessage: "Chyba pri vytváraní plánu." }));
    }
    setPlanModalOpen(false);
  };

  const deletePlan = (id) => {
    axios
      .delete(`${SUBSCRIPTION_PLANS_URL}/${id}`, authHeaders())
      .then(() => loadPlans())
      .catch((error) => handleApiError(error, { showAlert: true, fallbackMessage: "Chyba pri mazaní plánu." }));
    setDeletePlanConfirm(null);
  };

  // ── Subscription CRUD ──
  // Najprv si spravíme unikátny zoznam užívateľov (posledný výskyt mailu vyhráva)
  const uniqueUsers = [...new Map(users.map((u) => [u.email, u])).values()];

  // Teraz aplikujeme tvoju logiku
  const usersWithoutActiveSub = uniqueUsers.filter((u) => {
    if (!u.is_verified) return false;

    return !subscriptions.some(
      (s) => s.user_id === u.id && getSubStatus(s) === "active",
    );
  });

  // Zľava (%) užívateľa vybraného v modáli – premieta sa do cien plánov
  const subUserDiscount =
    users.find((u) => u.id === parseInt(subForm.user_id, 10))?.discount ?? 0;

  const openAddSub = () => {
    setSubForm({
      user_id: "",
      plan_id: "",
      valid_from: "",
      valid_to: "",
      payment_type: "",
    });
    setSubModalOpen(true);
  };

  const handleSubSubmit = (e) => {
    e.preventDefault();
    const user = users.find((u) => u.id === parseInt(subForm.user_id, 10));
    const plan = plans.find((p) => p.id === parseInt(subForm.plan_id, 10));

    // Vlastný Select nemá natívnu validáciu "required"
    if (!user || !plan || !subForm.valid_from) {
      showErrorModal("Vyberte užívateľa, plán a dátum od.");
      return;
    }
    if (!subForm.payment_type) {
      showErrorModal("Vyberte typ platby – určuje číselný rad dokladu.");
      return;
    }
    if (!isAddressComplete(user)) {
      showErrorModal(
        "Užívateľ nemá vyplnenú celú adresu. Doplňte ju v sekcii Užívatelia.",
      );
      return;
    }

    const amount = getDiscountedPrice(plan.price, user.discount);
    if (!(amount > 0)) {
      showErrorModal(
        "Výsledná cena je 0 – predplatné s nulovou sumou sa nedá vytvoriť.",
      );
      return;
    }

    const data = {
      user_id: user.id,
      plan_id: plan.id,
      valid_from: subForm.valid_from,
      valid_to: validFromToValidTo(subForm.valid_from, plan.duration ?? 0),
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      amount,
      currency: plan.currency,
      productName: plan.name,
      transactionId: `${subForm.payment_type}-${Date.now()}`,
    };

    axios
      .put(MANUAL_ADD_SUBSCRIPTION_URL, data, authHeaders())
      .then(() => loadSubscriptions())
      .catch((error) => {
        handleApiError(error, { showAlert: true, fallbackMessage: "Chyba pri vytváraní predplatného." });
      });
    setSubModalOpen(false);
  };


  const openEditSub = (sub) => {
    setEditSubForm({
      payment_id: sub.payment_id,
      user_id: sub.user_id,
      plan_name: sub.plan_name || sub.plan_id,
      valid_from: formatDateForBackend(new Date(sub.valid_from)),
      valid_to: formatDateForBackend(new Date(sub.valid_to)),
    });
    setEditSubModalOpen(true);
  };

  const handleEditSubSubmit = (e) => {
    e.preventDefault();
    if (!editSubForm.valid_from || !editSubForm.valid_to) return;
    setSubmittingEdit(true);

    axios
      .post(
        UPDATE_SUBSCRIPTION_DATES_URL,
        {
          payment_id: editSubForm.payment_id,
          valid_from: editSubForm.valid_from,
          valid_to: editSubForm.valid_to,
        },
        authHeaders(),
      )
      .then(() => {
        setEditSubModalOpen(false);
        loadSubscriptions();
      })
      .catch((error) => {
        handleApiError(error, {
          showAlert: true,
          fallbackMessage: "Chyba pri úprave dátumov predplatného.",
        });
      })
      .finally(() => {
        setSubmittingEdit(false);
      });
  };

  const refundSub = (id) => {
    if (refunding) return;
    setRefunding(true);
    const sub = subscriptions.find((s) => s.payment_id === id);
    const isManual =
      sub?.stripe_invoice_id?.includes("cash") ||
      sub?.stripe_invoice_id?.includes("transfer");
    const url = isManual
      ? REFUND_MANUAL_SUBSCRIPTION_URL
      : REFUND_SUBSCRIPTION_URL;

    axios
      .post(url, { payment_id: id }, authHeaders())
      .then(() => loadSubscriptions())
      .catch((error) => handleApiError(error, { showAlert: true, fallbackMessage: "Chyba pri refundácii predplatného." }))
      .finally(() => {
        setRefunding(false);
        setRefundSubConfirm(null);
      });
  };

  return (
    <div className="admin__section">
      <h2 className="admin__section-title">Predplatné</h2>

      {/* ── Plans table ── */}
      <div className="admin__sub-header">
        <h3 className="admin__section-subtitle">Plány predplatného</h3>
        <Button variant="primary" size="sm" onClick={openAddPlan}>
          <Plus size={16} /> Pridať plán
        </Button>
      </div>
      <Table
        columns={[
          {
            key: "name",
            label: "Názov",
            render: (val) => <strong>{val}</strong>,
          },
          {
            key: "price",
            label: "Cena",
            render: (val) => parseFloat(val).toFixed(2),
          },
          { key: "currency", label: "Mena", render: (val) => val || "EUR" },
          { key: "duration", label: "Trvanie (mesiace)" },
          { key: "description", label: "Popis", render: (val) => val || "—" },
          { key: "poradie", label: "Poradie", render: (val) => val ?? "—" },
          {
            key: "favorite",
            label: "Obľúbené",
            render: (val) => (val ? "Áno" : "Nie"),
          },
          {
            key: "test_period",
            label: "Skúšobná",
            render: (val) => (val ? "Áno" : "Nie"),
          },
          {
            key: "actions",
            label: "Akcie",
            sortable: false,
            render: (_, row) => (
              <div className="admin__table-actions">
                <button
                  className="admin__icon-btn"
                  onClick={() => openEditPlan(row)}
                  title="Upraviť"
                >
                  <Pencil size={16} />
                </button>
                <button
                  className="admin__icon-btn admin__icon-btn--danger"
                  onClick={() => setDeletePlanConfirm(row.id)}
                  title="Zmazať"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ),
          },
        ]}
        data={plans}
        searchPlaceholder="Hľadať plán..."
        emptyMessage="Žiadne plány"
      />

      {/* ── Subscriptions table ── */}
      <div className="admin__sub-header">
        <h3 className="admin__section-subtitle">Predplatné užívateľov</h3>
        <Button variant="primary" size="sm" onClick={openAddSub}>
          <Plus size={16} /> Pridať predplatné
        </Button>
      </div>
      <Table
        columns={[
          {
            key: "user_id",
            label: "Užívateľ",
            sortable: false,
            render: (val) => {
              const user = users.find((u) => u.id === val);
              if (!user) return val;
              const fullName =
                `${user.firstName || ""} ${user.lastName || ""}`.trim();
              return (
                <div>
                  <div className="admin__user-name">
                    {fullName || user.email}
                  </div>
                  <div className="admin__user-email">{user.email}</div>
                </div>
              );
            },
          },
          { key: "plan_name", label: "Plán", render: (val) => getPlanName(val) },
          {
            key: "stripe_invoice_id",
            label: "ID transakcie",
            render: (val) =>
              val ? (
                <span className="admin__cell--truncate" title={val}>
                  {val}
                </span>
              ) : (
                "—"
              ),
          },
          {
            key: "valid_from",
            label: "Platí od",
            render: (val) => new Date(val).toLocaleDateString("sk-SK"),
          },
          {
            key: "valid_to",
            label: "Platí do",
            render: (val) => new Date(val).toLocaleDateString("sk-SK"),
          },
          {
            key: "status",
            label: "Stav",
            sortable: false,
            render: (_, row) => {
              const status = getSubStatus(row);
              return (
                <span
                  className={`admin__status-badge ${status === "active"
                    ? "admin__status-badge--active"
                    : status === "pending"
                      ? "admin__status-badge--pending"
                      : ""
                    }`}
                >
                  {statusLabel(status)}
                </span>
              );
            },
          },
          {
            key: "actions",
            label: "Akcie",
            sortable: false,
            render: (_, row) => {
              const status = getSubStatus(row);
              return (
                <div className="admin__table-actions">
                  <button
                    className="admin__icon-btn"
                    onClick={() => openEditSub(row)}
                    title="Upraviť dátumy"
                  >
                    <Pencil size={16} />
                  </button>
                  {status === "active" && (
                    <button
                      className="admin__icon-btn admin__icon-btn--danger"
                      onClick={() => {
                        setRefundSubConfirm(row.payment_id);
                      }}
                      title="Refundovať"
                    >
                      <RotateCcw size={16} />
                    </button>
                  )}
                </div>
              );
            },
          },
        ]}
        data={subscriptions}
        searchPlaceholder="Hľadať predplatné..."
        emptyMessage="Žiadne predplatné"
      />

      {/* ── Plan modal (add/edit) ── */}
      <Modal
        isOpen={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        title={editingPlan ? "Upraviť plán" : "Pridať plán"}
      >
        <form className="admin__form" onSubmit={handlePlanSubmit}>
          <div className="admin__form-field">
            <label className="admin__form-label">Názov</label>
            <input
              maxLength={100}
              className="admin__form-input"
              value={planForm.name}
              onChange={(e) =>
                setPlanForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Napr. Premium"
              required
            />
          </div>
          <div className="admin__form-row">
            <div className="admin__form-field">
              <label className="admin__form-label">Cena</label>
              <input
                className="admin__form-input"
                type="number"
                step="0.01"
                min="0"
                value={planForm.price}
                onChange={(e) =>
                  setPlanForm((prev) => ({
                    ...prev,
                    price: parseFloat(e.target.value),
                  }))
                }
                placeholder="29.00"
                required
              />
            </div>
            <div className="admin__form-field">
              <label className="admin__form-label">Mena</label>
              <input
                maxLength={10}
                className="admin__form-input"
                value={planForm.currency}
                onChange={(e) =>
                  setPlanForm((prev) => ({
                    ...prev,
                    currency: e.target.value,
                  }))
                }
                placeholder="EUR"
                required
              />
            </div>
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">
              Trvanie (počet mesiacov)
            </label>
            <input
              className="admin__form-input"
              type="number"
              min="1"
              max="60"
              value={planForm.duration}
              onChange={(e) =>
                setPlanForm((prev) => ({
                  ...prev,
                  duration: parseInt(e.target.value, 10),
                }))
              }
              placeholder="9"
              required
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Popis</label>
            <input
              maxLength={255}
              className="admin__form-input"
              value={planForm.description}
              onChange={(e) =>
                setPlanForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Krátky popis výhod balíka"
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Poradie</label>
            <input
              className="admin__form-input"
              type="number"
              value={planForm.poradie}
              onChange={(e) =>
                setPlanForm((prev) => ({
                  ...prev,
                  poradie: parseInt(e.target.value, 10),
                }))
              }
              placeholder="Poradie"
            />
          </div>
          <div className="admin__form-field-checkbox">
            <label className="admin__form-label">Obľúbené</label>
            <input
              className="admin__form-checkbox"
              type="checkbox"
              checked={planForm.favorite}
              onChange={(e) =>
                setPlanForm((prev) => ({
                  ...prev,
                  favorite: e.target.checked,
                }))
              }
            />
          </div>
          <div className="admin__form-field-checkbox">
            <label className="admin__form-label">Skúšobná perióda</label>
            <input
              className="admin__form-checkbox"
              type="checkbox"
              checked={planForm.test_period}
              onChange={(e) =>
                setPlanForm((prev) => ({
                  ...prev,
                  test_period: e.target.checked,
                }))
              }
            />
          </div>
          <div className="admin__form-actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setPlanModalOpen(false)}
            >
              Zrušiť
            </Button>
            <Button variant="primary" type="submit">
              {editingPlan ? "Uložiť zmeny" : "Pridať plán"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Delete plan confirmation ── */}
      <Modal
        isOpen={deletePlanConfirm !== null}
        onClose={() => setDeletePlanConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať tento plán? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeletePlanConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={() => deletePlan(deletePlanConfirm)}
            style={{ backgroundColor: "#EF4444", bporadieColor: "#EF4444" }}
          >
            Zmazať
          </Button>
        </div>
      </Modal>

      {/* ── Subscription modal (add) ── */}
      <Modal
        isOpen={subModalOpen}
        onClose={() => setSubModalOpen(false)}
        title="Pridať predplatné"
      >
        <form className="admin__form" onSubmit={handleSubSubmit}>
          <div className="admin__form-field">
            <Select
              label="Užívateľ"
              placeholder="Vyberte užívateľa"
              value={subForm.user_id}
              required
              onChange={(val) =>
                setSubForm((prev) => ({ ...prev, user_id: val }))
              }
              options={usersWithoutActiveSub.map((u) => {
                const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
                const label = fullName ? `${fullName} (${u.email})` : u.email;
                const hasAddress = isAddressComplete(u);
                return {
                  value: u.id,
                  label: hasAddress ? label : `${label} – chýba adresa`,
                  disabled: !hasAddress,
                };
              })}
            />
          </div>
          <div className="admin__form-field">
            <Select
              label="Plán"
              placeholder="Vyberte plán"
              value={subForm.plan_id}
              required
              onChange={(val) =>
                setSubForm((prev) => ({ ...prev, plan_id: val }))
              }
              options={plans.map((p) => {
                const price = getDiscountedPrice(p.price, subUserDiscount);
                const label = `${p.name} — ${price.toFixed(2)} ${p.currency || "EUR"}`;
                return {
                  value: p.id,
                  label:
                    subUserDiscount > 0
                      ? `${label} (zľava ${subUserDiscount} %)`
                      : label,
                };
              })}
            />
          </div>
          <div className="admin__form-field">
            {subForm.valid_from}
            <DatePicker
              label="Dátum od"
              value={subForm.valid_from}
              placeholder="Vyber dátum..."
              onChange={(val) =>
                setSubForm((prev) => ({
                  ...prev,
                  valid_from: val?.target?.value !== undefined ? val.target.value : val,
                }))
              }
              required
            />
          </div>
          <div className="admin__form-field">
            <Select
              label="Typ platby"
              placeholder="Vyberte typ platby"
              required
              value={subForm.payment_type}
              onChange={(val) =>
                setSubForm((prev) => ({ ...prev, payment_type: val }))
              }
              options={[
                { value: "cash", label: "Hotovosť" },
                { value: "transfer", label: "Prevod" },
              ]}
            />
          </div>
          <div className="admin__form-actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setSubModalOpen(false)}
            >
              Zrušiť
            </Button>
            <Button variant="primary" type="submit">
              Pridať predplatné
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Subscription modal (edit dates) ── */}
      <Modal
        isOpen={editSubModalOpen}
        onClose={() => setEditSubModalOpen(false)}
        title="Upraviť predplatné"
      >
        <form className="admin__form" onSubmit={handleEditSubSubmit}>
          <div className="admin__form-field">
            <label className="admin__form-label">Užívateľ</label>
            <input
              className="admin__form-input"
              value={getUserName(editSubForm.user_id)}
              disabled
              readOnly
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Plán</label>
            <input
              className="admin__form-input"
              value={getPlanName(editSubForm.plan_name)}
              disabled
              readOnly
            />
          </div>
          <div className="admin__form-field">
            <DatePicker
              label="Platí od"
              value={editSubForm.valid_from}
              placeholder="Vyber dátum..."
              onChange={(val) =>
                setEditSubForm((prev) => ({
                  ...prev,
                  valid_from:
                    val?.target?.value !== undefined ? val.target.value : val,
                }))
              }
              required
            />
          </div>
          <div className="admin__form-field">
            <DatePicker
              label="Platí do"
              value={editSubForm.valid_to}
              placeholder="Vyber dátum..."
              onChange={(val) =>
                setEditSubForm((prev) => ({
                  ...prev,
                  valid_to:
                    val?.target?.value !== undefined ? val.target.value : val,
                }))
              }
              required
            />
          </div>
          <div className="admin__form-actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setEditSubModalOpen(false)}
            >
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" disabled={submittingEdit}>
              {submittingEdit ? "Ukladám..." : "Uložiť zmeny"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Refund subscription confirmation ── */}
      <Modal
        isOpen={refundSubConfirm !== null}
        onClose={() => setRefundSubConfirm(null)}
        title="Potvrdiť refundáciu"
      >
        <p>
          Naozaj chcete refundovať toto predplatné? Táto akcia sa nedá vrátiť.
        </p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setRefundSubConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            // da sa refundovať iba ak ma status active
            disabled={
              refunding ||
              !subscriptions.find(
                (s) =>
                  s.payment_id === refundSubConfirm &&
                  getSubStatus(s) === "active",
              )
            }
            onClick={() => refundSub(refundSubConfirm)}
            style={{ backgroundColor: "#EF4444", borderColor: "#EF4444" }}
          >
            {refunding ? "Refundujem..." : "Refundovať"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default SubscriptionsSection;
